<template>
  <v-form class="helmi-form">
    <v-row v-if="items">
      <v-col cols="12">
        <v-expansion-panels
          v-model="activePanel"
          v-bind="$attrs"
          focusable
          @change="onChange"
        >
          <component
            :is="item.component"
            v-for="(item, key, index) in items"
            :id="`${id}-${key}`"
            :key="`${id}__${item.chapter}_${index}`"
            v-model="item.value"
            v-bind="{ ...options.fieldProps, ...item.props }"
            :active="index === activePanel"
            :classes="item.classes || ''"
            :container-props="item.container"
            :container-options="options.containerProps"
            :options="options"
            :items="item.options"
            :children="item.children"
            :chapter="item.chapter"
            :colors="colors"
            @evaluate="evaluateForm"
          />
        </v-expansion-panels>
      </v-col>
    </v-row>
  </v-form>
</template>

<script>
/* eslint-disable no-param-reassign */
import { EventBus, sleep } from '@/utils';

export default {
  name: 'HelmiForm',

  components: {
    'helmi-section': () => import('@/components/formElements/Section'),
    'helmi-radiogroup': () => import('@/components/formElements/RadioGroup'),
    'helmi-select': () => import('@/components/formElements/Select'),
    'helmi-text-field': () => import('@/components/formElements/TextField'),
    'helmi-textarea': () => import('@/components/formElements/TextArea'),
    'helmi-title': () => import('@/components/formElements/Title'),
  },

  props: {
    activeIndex: {
      type: [Number, Object],
      default: null,
      validator: (prop) => {
        if (prop === Object(prop)) {
          return prop === null;
        }

        return true;
      },
    },

    id: {
      type: String,
      required: true,
    },

    items: {
      type: Object,
      default: () => ({}),
    },

    options: {
      type: Object,
      default: () => ({}),
    },

    children: {
      type: Object,
      default: () => ({}),
    },

    color: {
      type: String,
      default: 'primary',
    },
  },

  data: () => ({
    activePanel: null,
    modelValue: '',
  }),

  computed: {
    colors() {
      const colors = this.color.split(' ');
      const isShade = c => c.includes('lighten') || c.includes('darken');
      const baseValues = [];
      const textValues = [];

      for (const color of colors) {
        baseValues.push(color);
        textValues.push(isShade(color) ? `text--${color}` : `${color}--text`);
      }

      return {
        base: baseValues.join(' '),
        text: textValues.join(' '),
      };
    },
  },

  watch: {
    activeIndex: {
      handler(index) {
        if (!index && index < 0) return;

        this.activePanel = index;
      },
      immediate: true,
    },

    // Handles internal model changes.
    modelValue(newVal) {
      this.$emit('input', newVal);
    },

    // Handles external model changes.
    '$attrs.value': {
      handler(val) {
        if (!val) return;
        this.modelValue = val;
      },
      immediate: true,
    },
  },

  mounted() {
    EventBus.$on('PANEL_TOGGLED', this.onEventRecieved);
  },

  methods: {
    /**
     * Evaluate a condition based on a value
     *
     * @param {Sring|Number} value
     * @param {Object} condition
     *
     * @returns {Boolean} - was the condition met
     */
    evaluateCondition(value, condition) {
      if (!condition) return '';

      // the evaluation cases
      const evaluate = {
        '>': v => v > condition.value,
        '>=': v => v >= condition.value,
        '<': v => v < condition.value,
        '<=': v => v <= condition.value,
        '=': v => v === condition.value,
        '!=': v => v !== condition.value,
      };

      return evaluate[condition.type](value);
    },

    /**
     * evaluate through all the conditions and hide elements not meeting their requirements
     *
     * @param {String|Number} value - Value to evaluate against
     * @param {String} key - Key of the evaluated property (in the form items)
     */
    evaluateForm(value, field) {
      const finder = (obj, searchTerm) => {
        for (const key of Object.keys(obj)) {
          // if item has a condition involving the searched field
          if (
            obj[key]
            && obj[key].condition
            && obj[key].condition.item === searchTerm
          ) {
            const valid = this.evaluateCondition(value, obj[key].condition);
            // hide the field if condition is not met
            obj[key].hidden = !valid;
            // wipe the value of a hidden field
            obj[key].value = valid ? obj[key].value : '';
          } else if (this.isAnObject(obj[key])) {
            finder(obj[key], searchTerm);
          }
        }
      };

      finder(this.items, field);
    },

    onEventRecieved(id, index) {
      if (id !== this.id) {
        this.activePanel = null;
        return;
      }

      const target = `${id}-${Object.keys(this.items)[index]}-anchor`;

      //  scroll to opened expansion item
      this.scrollToItem(target);
    },

    isAnObject(item) {
      return item === Object(item) && !Array.isArray(item);
    },

    onChange(e) {
      EventBus.$emit('PANEL_TOGGLED', this.id, e);
    },

    /**
     * Scroll element to the top on open
     */
    async scrollToItem(target) {
      if (!target) return;

      const element = window.document.getElementById(target);

      if (!element) return;

      await sleep(600);

      window.requestAnimationFrame(() => element.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      }));
    },
  },
};
</script>

<style lang="scss">
.helmi-form {
  max-width: 1024px;
  position: relative;

  .v-input--selection-controls {
    margin-top: 0;
    padding-top: 0;
  }

  .v-input--checkbox {
    .v-input__slot {
      align-items: start;

      > .v-label {
        top: 2px;
      }
    }
  }

  .v-expansion-panel,
  .v-expansion-panels,
  .v-expansion-panel::before,
  .v-expansion-panel::after,
  .v-expansion-panel-content,
  .v-expansion-panel-content__wrap,
  .v-expansion-panel-header,
  .v-expansion-panel-header::before {
    transition-duration: 0.15s;
  }
}

.helmi-form-element--hidden {
  display: none !important;
}

// fix for the label element height bug for Safari etc.
.v-input {
  legend.v-label {
    height: auto;
    min-height: 20px;
  }
}
</style>
